import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { formatRentalPeriod, cn } from '../lib/utils';
import { computeJobFinancials } from '../lib/jobFinancials';
import { clientStockStyle, clientMonogram } from '../lib/cardStock';
import { useAppStore } from '../store/appStore';
import './JobCard.css';

// Papers sit 94px apart, in a row from the card's left edge
const PAPER_GAP = 94;

// Phase type -> stamp. Wet weather holds are not days worked, so they get none.
const STAMP_FOR_PHASE = { pre: 'prep', production: 'shoot', post: 'post', travel: 'travel' };
const STAMP_WORD = { prep: 'prep', shoot: 'shoot', post: 'post', travel: 'travel' };
const MAX_STAMPS = 14;

function plural(n, word) {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

function trimDays(n) {
  return parseFloat(Number(n).toFixed(1));
}

/** The job's days as stamps, in phase order, plus counts for the label. */
function dayStamps(job) {
  const stamps = [];
  const counts = {};
  (job.phases || []).forEach((phase) => {
    const kind = STAMP_FOR_PHASE[phase.type];
    const days = Number(phase.estimated_days) || 0;
    if (!kind || days <= 0) return;
    counts[kind] = (counts[kind] || 0) + days;
    for (let i = 0; i < Math.ceil(days); i += 1) stamps.push(kind);
  });
  return { stamps, counts };
}

function rentalDays(job) {
  if (!(job.equipment_rental_only && job.rental_start_date && job.rental_end_date)) return 0;
  return Math.round((new Date(job.rental_end_date) - new Date(job.rental_start_date)) / 86400000) + 1;
}

/**
 * A job as a pass printed on its client's card stock. Its papers - the brief,
 * the estimate, the equipment list and any treatment, only those that exist -
 * peek over its top edge and rise out of it on hover or keyboard focus, so
 * each is one click from the board.
 *
 * Used on the Jobs board and the Dashboard. On a phone it shows only the
 * client and the job's name (JobCard.css).
 *
 * Props:
 *   job            - the job
 *   formatCurrency - from useRegion()
 *   brief          - (optional) the job's brief, for its status on the paper
 *   treatments     - (optional) treatments linked to this job
 *   stackIndex     - (optional) place in the Dashboard's stacked deck. A stacked
 *                    card is drawn flat, without its papers.
 *   totalCards     - (optional) cards in that deck
 */
export function JobCard({ job, formatCurrency, brief = null, treatments = [], stackIndex = null, totalCards = null }) {
  const [busy, setBusy] = useState(null);
  const downloadQuotePDF = useAppStore((s) => s.downloadQuotePDF);
  const downloadJobEquipmentListPDF = useAppStore((s) => s.downloadJobEquipmentListPDF);
  const isStacked = stackIndex !== null;

  const name = job.project_name || 'Untitled job';
  const client = job.client || 'No client';
  const { profit } = computeJobFinancials(job);
  const total = Number(job.totals?.grand) || 0;
  const { stamps, counts } = dayStamps(job);
  const rental = rentalDays(job);
  const crewCount = (job.crew_labour || []).length;

  const scopeParts = [];
  if (job.equipment_rental_only) {
    if (rental > 0) scopeParts.push(`${plural(rental, 'day')} equipment out`);
  } else {
    if (counts.shoot) scopeParts.push(plural(trimDays(counts.shoot), 'shoot day'));
    else if (stamps.length) scopeParts.push(plural(trimDays(Object.values(counts).reduce((a, b) => a + b, 0)), 'day'));
  }
  if (crewCount) scopeParts.push(`${crewCount} crew`);
  const scope = scopeParts.join(', ');

  const stampLabel = stamps.length
    ? `${plural(trimDays(Object.values(counts).reduce((a, b) => a + b, 0)), 'production day')}: ${Object.entries(counts)
        .map(([k, n]) => `${trimDays(n)} ${STAMP_WORD[k]}`)
        .join(', ')}`
    : '';

  const runPdf = async (kind, fn, label) => {
    if (busy) return;
    setBusy(kind);
    const toastId = toast.loading(`Preparing the ${label}`);
    try {
      await fn(job.id);
      toast.success(`${label[0].toUpperCase()}${label.slice(1)} downloaded`, { id: toastId });
    } catch {
      toast.error(`Couldn't download the ${label}`, { id: toastId });
    } finally {
      setBusy(null);
    }
  };

  // Only the papers that exist come out of the pocket
  const docs = [];
  if (job.brief_id) {
    docs.push({ key: 'brief', kind: 'Brief', meta: brief?.status ? brief.status.replace(/_/g, ' ') : 'Open', to: `/briefs/${job.brief_id}`, lines: true, label: `Open the brief for ${name}` });
  }
  docs.push({
    key: 'estimate', kind: 'Estimate', meta: formatCurrency(total), pdf: true, label: `Download the estimate PDF for ${name}`,
    onClick: () => runPdf('estimate', downloadQuotePDF, 'estimate'),
  });
  const equipmentCount = (job.equipment || []).length;
  if (equipmentCount > 0) {
    docs.push({
      key: 'equipment', kind: 'Equipment', meta: plural(equipmentCount, 'item'), pdf: true, label: `Download the equipment list PDF for ${name}`,
      onClick: () => runPdf('equipment', downloadJobEquipmentListPDF, 'equipment list'),
    });
  }
  if (treatments.length > 0) {
    const t = treatments[0];
    const shots = (t.shots || []).length;
    docs.push({ key: 'treatment', kind: 'Shot list', meta: shots ? plural(shots, 'shot') : t.title, to: `/treatments/${t.id}`, frames: true, label: `Open the shot list for ${name}` });
  }
  // Keep the order Brief, Estimate, Equipment, Treatment, left to right
  docs.forEach((d, i) => { d.x = `${i * PAPER_GAP}px`; });

  const face = (
    <Link
      to={`/jobs/${job.id}`}
      className="jc-face"
      draggable={false}
      aria-label={`Open ${name}`}
      data-testid={`job-card-${job.id}`}
    >
      <div className="jc-top">
        <span className="jc-brand">
          <span className="jc-mono" aria-hidden="true">{clientMonogram(job.client)}</span>
          <span>{client}</span>
        </span>
      </div>
      <div>
        <span className="jc-num">{job.job_number || `#${job.id?.slice(0, 6).toUpperCase()}`}</span>
        <h3 className="jc-name">
          {name}
          {job.equipment_rental_only && job.rental_start_date && job.rental_end_date
            ? `: ${formatRentalPeriod(job.rental_start_date, job.rental_end_date)}`
            : ''}
        </h3>
      </div>
      {scope && <span className="jc-scope">{scope}</span>}
      {stamps.length > 0 && (
        <span className="jc-stamps" role="img" aria-label={stampLabel}>
          {stamps.slice(0, MAX_STAMPS).map((kind, i) => (
            <span key={i} className={cn('jc-stamp', kind)} />
          ))}
          {stamps.length > MAX_STAMPS && <span className="jc-stamp-more">+{stamps.length - MAX_STAMPS}</span>}
        </span>
      )}
      <span className="jc-perf" aria-hidden="true" />
      <span className="jc-stub">
        <span className="jc-field"><i>total</i><b>{formatCurrency(total)}</b></span>
        <span className="jc-field end">
          <i>profit</i>
          <b>{profit > 0 ? '+' : ''}{formatCurrency(profit)}</b>
        </span>
      </span>
    </Link>
  );

  if (isStacked) {
    // The Dashboard's deck: cards fanned behind one another, drawn flat
    const style = {
      ...clientStockStyle(job.client),
      position: 'absolute',
      inset: 0,
      transform: `translateY(${stackIndex * 12}px) translateX(${stackIndex * 6}px) rotate(${stackIndex * 2}deg) scale(${1 - stackIndex * 0.03})`,
      zIndex: totalCards - stackIndex,
      opacity: stackIndex === 0 ? 1 : 0.85 - stackIndex * 0.1,
      transformOrigin: 'top left',
      transition: 'transform 0.3s ease-out',
    };
    return <div className="jc jc--plain" style={style}>{face}</div>;
  }

  return (
    <div className="jc" style={clientStockStyle(job.client)}>
      {face}
      <div className="jc-docs">
        {docs.map((d) => {
          const body = (
            <>
              <span className="jc-doc-kind">{d.kind}</span>
              <span className="jc-doc-meta">{d.meta}</span>
              {d.pdf && <span className="jc-doc-chip">{busy === d.key ? '…' : 'PDF'}</span>}
              {d.lines && (
                <>
                  <span className="jc-doc-line" style={{ width: '90%' }} />
                  <span className="jc-doc-line" style={{ width: '64%' }} />
                  <span className="jc-doc-line" style={{ width: '78%' }} />
                </>
              )}
              {d.frames && (
                <span className="jc-doc-frames" aria-hidden="true">
                  <span style={{ background: 'hsl(var(--cat-grip))' }} />
                  <span style={{ background: 'hsl(var(--cat-lighting))' }} />
                  <span style={{ background: 'hsl(var(--cat-lenses))' }} />
                  <span style={{ background: 'hsl(var(--cat-monitors))' }} />
                </span>
              )}
            </>
          );
          return d.to ? (
            <Link key={d.key} to={d.to} className="jc-doc" style={{ '--x': d.x }} aria-label={d.label} draggable={false} data-testid={`job-card-doc-${d.key}-${job.id}`}>
              {body}
            </Link>
          ) : (
            <button key={d.key} type="button" className="jc-doc" style={{ '--x': d.x }} aria-label={d.label} aria-busy={busy === d.key} onClick={d.onClick} data-testid={`job-card-doc-${d.key}-${job.id}`}>
              {body}
            </button>
          );
        })}
      </div>
    </div>
  );
}
